import React, { FunctionComponent, useState, useEffect, Suspense, useCallback } from 'react';
import { lazyWithPreload as lazyRoute } from '@/perf/lazyWithPreload';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
// Import por path específico (NO el barrel '@/containers'): el barrel no declara
// sideEffects:false, así que importarlo evalúa TODOS sus re-exports —incluido
// './Clients'— y los mete en el index aunque acá Clients sea lazy. Yendo directo
// al módulo, el barrel nunca se evalúa y Clients/AddClient/ClientDetail quedan
// realmente lazy (su propio chunk).
import { ScrollToTop } from '@/components';
import { useAuth } from '@/contexts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { isUpHiddenCatalogPath } from '@/utils/agora-up-market';
// Desde `Finanzas/types` (sin imports) y NO desde el contenedor: importarlo del
// contenedor metía TODO Finanzas en el bundle eager del Router.
import { FINANZAS_ADDON_TABS } from '@/containers/Finanzas/types';
import { PinEntryModal, PinProtectedRoute } from '@/components/PinProtection';
// Path específico y no el barrel '@/components', por lo mismo del comentario de arriba.
const GodModeForm = lazyRoute(() =>
  import('@/containers/Settings/GodMode').then((m) => ({ default: m.GodModeForm }))
);
// Lazy a propósito: es una pantalla de borde (cuenta de CLIENTE que aterriza en
// Flash), pero al ser eager metía su EmptyState en el bundle de arranque de toda
// la app. Mismo criterio que las rutas de arriba.
const CustomerAccountGate = lazyRoute(() =>
  import('@/components/CustomerAccountGate').then((m) => ({ default: m.CustomerAccountGate }))
);
import { isStaffAllowedPath } from '@/utils/staffRoutes';
import { PrivateRoute } from './PrivateRoute';
import { useAuth0 } from '@auth0/auth0-react';
import { SessionLoader } from './SessionLoader';
import { AgoraPlanNameE, Auth0RolesE } from '@/types/cyclone/models';
import { useLocalStorage } from '@/hooks';
// Onboarding (alta y academia): providers y gates LAZY. Solo los renderiza un vendor
// que todavía no terminó el onboarding, pero importados estáticamente metían
// react-hook-form + yup + ~25 KB de fuente propia en el arranque de TODAS las cargas.
// Los `load*` se reutilizan para precalentar los chunks apenas la sesión dice que el
// vendor está en onboarding (ver RouterContent), así no hay cascada de descargas.
const loadOnboardingProvider = () => import('@/containers/SelfOnboarding/contexts/onboarding');
const loadAcademyOnboardingProvider = () =>
  import('@/containers/AcademyOnboarding/contexts/academyOnboarding');
const loadOnboardingVariantSwitch = () => import('@/components/Router/OnboardingVariantSwitch');
const OnboardingProvider = lazyRoute(() =>
  loadOnboardingProvider().then((m) => ({ default: m.OnboardingProvider }))
);
const AcademyOnboardingProvider = lazyRoute(() =>
  loadAcademyOnboardingProvider().then((m) => ({ default: m.AcademyOnboardingProvider }))
);
const OnboardingVariantSwitch = lazyRoute(() =>
  loadOnboardingVariantSwitch().then((m) => ({ default: m.OnboardingVariantSwitch }))
);
const OnboardingWelcomeOrSkip = lazyRoute(() =>
  loadOnboardingVariantSwitch().then((m) => ({ default: m.OnboardingWelcomeOrSkip }))
);
// Import directo del módulo liviano (solo localStorage) — NO arrastra el chunk lazy
// de Agenda al bundle principal. Limpia el día/semana/mes recordado al salir de /agenda.
import { clearPersistedAgendaDate } from '@/containers/Agenda/agendaDateMemory';
import { Logout } from '@/containers/Logout';
import { initAnalytics, initPublicAnalytics, trackEventBeacon } from '@/analytics';
import { trackAuthEvent, AUTH_EVENTS } from '@/services/analytics/featureUsage';
import { getBootSnapshot, describeBootstrapFailure } from '@/auth/sessionDiagnostics';
import { isCodeCallbackAtBoot } from '@/auth/freshSession';

// Una falla SIN evidencia de sesión previa puede ser dos cosas indistinguibles
// desde el cliente: un vendor al que WebKit le barrió el storage (el caso que
// queremos medir) o un visitante anónimo cualquiera. Se separan después por
// persona: la que ya tenía logins es un vendor real.
//
// Al 100% a propósito. Arrancó en 0.25 por miedo a la cuota, pero medido en prod
// ese slice da CERO eventos (los anónimos entran por /login, que no rebota). Y
// muestrearlo tenía un costo feo: las fallas CON evidencia van al 100%, así que
// un 0.25 acá metía un sesgo de 4x en contra de justamente la hipótesis que hay
// que poder refutar — la data mostraría "casi todo es caso A" por construcción.
// Si algún día aparece volumen anónimo de verdad, bajar este número (el evento
// sale con `sample_rate` para poder extrapolar).
//
// Apareció: 22/sep–5/oct/2026 el slice SIN sesión previa fue 5.910 eventos en 13
// días (~13k/mes, 1.589 personas, 3,7 por persona) contra 976 del slice CON sesión,
// y el proyecto ya se había pasado del millón el ciclo anterior. Va al 25%: al
// comparar los dos slices hay que ponderar con `sum(1 / sample_rate)` — contar
// filas crudas reintroduce el sesgo de 4x de arriba. El insight guardado
// (`Auth · bootstrap fallido con sesión previa`) filtra had_prior_session = true,
// que sigue al 100%, así que no se mueve.
const ANON_FAILURE_SAMPLE_RATE = 0.25;
const sampleFailure = (hadPriorSession: boolean): boolean =>
  hadPriorSession || Math.random() < ANON_FAILURE_SAMPLE_RATE;
// Lazy: las placas IG arrastraban framer-motion + ~45KB de src al entry que
// bajan TODAS las rutas. El pipeline de screenshots de cyclone espera al render
// igual, así que el chunk extra no lo afecta.
const Posts = lazyRoute(() => import('@/containers/Posts/Posts'));
const FeedPreview = lazyRoute(() => import('@/containers/Posts/FeedPreview'));
const ConnectMercadoPago = lazyRoute(() => import('@/containers/ProfessionalMercadoPago/ConnectMercadoPago'));
const AgoraUpLanding = lazyRoute(() => import('@/containers/AgoraUpLanding'));
const FrostTiersPlayground = lazyRoute(() => import('@/containers/Dev/FrostTiersPlayground'));
const TurnoHoverGallery = lazyRoute(() => import('@/containers/Agenda/dev/TurnoHoverGallery'));
const AgendaGestureGallery = lazyRoute(() => import('@/containers/Agenda/dev/AgendaGestureGallery'));
const StoryPreview = lazyRoute(() => import('@/containers/Posts/StoryPreview'));
const Login = lazyRoute(() => import('@/containers/Login/Login'));
const Auth0Callback = lazyRoute(() =>
  import('@/containers/Auth0Callback').then((module) => ({ default: module.Auth0Callback }))
);
const NewUser = lazyRoute(() =>
  import('@/containers/NewUser').then((module) => ({ default: module.NewUser }))
);
const TokenMonitor = lazyRoute(() =>
  import('@/containers/token_monitor').then((module) => ({ default: module.TokenMonitor }))
);
const TestErrorComponent = lazyRoute(() =>
  import('../App/TestError').then((module) => ({ default: module.TestErrorComponent }))
);
const NotFound = lazyRoute(() => import('@/containers/NotFound/NotFound'));
import { Loader } from '../Spinner/Loader';
// Leaf import, NO el barrel '@/components/Button': este módulo es eager y el barrel
// no tree-shakea (package.json sin sideEffects:false).
import { Button } from '../Button/Button';
import { Layout } from '../Layout';
import { ShellContentSkeleton } from '../Layout/ShellContentSkeleton';
import { RouteSkeleton } from './routeSkeletons';
import { TransitionBrowserRouter } from './TransitionBrowserRouter';
import { withReactProfiler } from '@/perf/reactProfiler';
import { hasPerfAccessToken } from '@/auth/perfTokenAuth';
import { registerRoutePreloads, warmRouteChunk } from '@/perf/routePreload';

// Acciones de agenda como rutas standalone (/agenda/ingresar-reserva,
// /agenda/bloquear-horarios, /agenda/horarios-de-trabajo): eran imports eager
// (~227KB) pese a alcanzarse solo al entrar a esas rutas. A React.lazy → su propio
// chunk; generateRoute ya da Suspense. Agenda no las importa internamente.
const ActionReservation = lazyRoute(() =>
  import('@/containers/Agenda/containers/ActionReservation').then((module) => ({
    default: module.ActionReservation
  }))
);
const ActionBlockHours = lazyRoute(() =>
  import('@/containers/Agenda/containers/ActionBlockHours').then((module) => ({
    default: module.ActionBlockHours
  }))
);
const ActionWorkingHours = lazyRoute(() =>
  import('@/containers/Agenda/containers/ActionWorkingHours').then((module) => ({
    default: module.ActionWorkingHours
  }))
);

// Rutas /descuentos (Coupon) y /ajustes/medios-de-cobro (PaymentGateways): eran
// imports eager → su código viajaba en el index. A React.lazy desde su módulo;
// generateRoute ya envuelve en <Suspense>.
const Coupon = lazyRoute(() => import('@/containers/Coupon').then((module) => ({ default: module.Coupon })));
const PaymentGateways = lazyRoute(() =>
  import('@/containers/PaymentGateways').then((module) => ({ default: module.PaymentGateways }))
);
// Sub-rutas /ajustes/medios-de-cobro/{mercadopago,efectivo,transferencia}: eran
// imports eager (viajaban en el index). A React.lazy → su propio chunk;
// generateRoute ya envuelve en <Suspense>. Sacan su FontAwesome del eager.
const BankTransfer = lazyRoute(() =>
  import('@/containers/PaymentGateways/containers').then((module) => ({ default: module.BankTransfer }))
);
const Cash = lazyRoute(() =>
  import('@/containers/PaymentGateways/containers').then((module) => ({ default: module.Cash }))
);
const MercadoPago = lazyRoute(() =>
  import('@/containers/PaymentGateways/containers').then((module) => ({ default: module.MercadoPago }))
);
// Página pública de ticket (/eventos/:id/registro) — eager innecesario en el index.
const InfoTicket = lazyRoute(() =>
  import('@/containers/InfoTicket').then((module) => ({ default: module.InfoTicket }))
);

// Familia de rutas /clientes: eran imports eager del barrel @/containers, así que
// todo el subtree de Clients (ClientsLegacy, FilterBar, SegmentsToolbar…)
// viajaba en el index que bajan TODAS las rutas. A React.lazy desde el módulo
// específico (no el barrel) → su propio chunk, cargado solo en /clientes.
// generateRoute ya envuelve en <Suspense>, mismo patrón que las demás rutas lazy.
const Clients = lazyRoute(() =>
  import('@/containers/Clients').then((module) => ({ default: module.Clients }))
);
const AddClient = lazyRoute(() =>
  import('@/containers/Clients').then((module) => ({ default: module.AddClient }))
);
const ClientSegments = lazyRoute(() =>
  import('@/containers/Clients').then((module) => ({ default: module.ClientSegments }))
);
const ClientDetail = lazyRoute(() =>
  import('@/containers/Clients').then((module) => ({ default: module.ClientDetail }))
);

// Rutas de onboarding/setup: solo las ve un vendor que aún NO terminó el onboarding
// (gateadas por shouldRestrictRoutes). Lazy → fuera del index chunk que bajan los
// 25k activos. Mismo patrón que las demás rutas lazy.
const CustomerJourneySetup = lazyRoute(
  () => import('@/containers/CustomerJourneySetup/containers/CustomerJourneySetup')
);
const AcademySetup = lazyRoute(() =>
  import('@/containers/AcademySetup').then((module) => ({ default: module.AcademySetupWithProvider }))
);

const Dashboard = lazyRoute(() =>
  import('@/containers/Dashboard').then((module) => ({ default: module.Dashboard }))
);
const Activities = lazyRoute(() =>
  import('@/containers/Activities').then((module) => ({ default: module.Activities }))
);
const LandingActivity = lazyRoute(() =>
  import('@/containers/LandingActivity').then((module) => ({ default: module.LandingActivity }))
);
const Team = lazyRoute(() => import('@/containers/Team').then((module) => ({ default: module.Team })));
const ActionTeam = lazyRoute(() =>
  import('@/containers/ActionTeam').then((module) => ({ default: module.ActionTeam }))
);
const CreateTeam = lazyRoute(() =>
  import('@/containers/CreateTeam').then((module) => ({ default: module.CreateTeam }))
);
const CreateVenue = lazyRoute(() =>
  import('@/containers/CreateVenue').then((module) => ({ default: module.CreateVenue }))
);
const TeamPayments = lazyRoute(() =>
  import('@/containers/TeamPayments').then((module) => ({ default: module.TeamPayments }))
);
const CashRegister = lazyRoute(() =>
  import('@/containers/CashRegister').then((module) => ({ default: module.CashRegister }))
);
const Finanzas = lazyRoute(() =>
  import('@/containers/Finanzas').then((module) => ({ default: module.Finanzas }))
);
const CashRegisterHistory = lazyRoute(() =>
  import('@/containers/CashRegister').then((module) => ({ default: module.CashRegisterHistory }))
);
const Agenda = lazyRoute(() => import('@/containers/Agenda').then((module) => ({ default: module.Agenda })));
const StoreFrontBuilder = lazyRoute(() =>
  import('@/containers/StoreFrontBuilder').then((module) => ({ default: module.StoreFrontBuilder }))
);
const Promoters = lazyRoute(() =>
  import('@/containers/Promoters').then((module) => ({ default: module.Promoters }))
);
const EventDetails = lazyRoute(() =>
  import('@/containers/EventDetails').then((module) => ({ default: module.EventDetails }))
);
const UniqueEvents = lazyRoute(() =>
  import('@/containers/UniqueEvents').then((module) => ({ default: module.UniqueEvents }))
);
const ActionUniqueEvent = lazyRoute(() =>
  import('@/containers/ActionUniqueEvent').then((module) => ({ default: module.ActionUniqueEvent }))
);
const Services = lazyRoute(() =>
  import('@/containers/Services').then((module) => ({ default: module.Services }))
);
const OrderingServices = lazyRoute(() =>
  import('@/containers/Services').then((module) => ({ default: module.OrderingServices }))
);
const AdjustmentsServices = lazyRoute(() =>
  import('@/containers/Services').then((module) => ({ default: module.AdjustmentsServices }))
);
const AdjustPrice = lazyRoute(() =>
  import('@/containers/Services/containers').then((module) => ({
    default: module.AdjustPrice
  }))
);
const AdjustPolicies = lazyRoute(() =>
  import('@/containers/Services/containers/AdjustmentsServices/containers').then((module) => ({
    default: module.AdjustPolicies
  }))
);
const AdjustAddress = lazyRoute(() =>
  import('@/containers/Services/containers/AdjustmentsServices/containers').then((module) => ({
    default: module.AdjustAddress
  }))
);
const AdjustDownpayment = lazyRoute(
  () =>
    import('@/containers/Services/containers/AdjustmentsServices/containers/AdjustDownpayment/AdjustDownpayment')
);
// Onboarding UI leaves: only new users (pre-onboarding) ever render these. Imported
// via direct module paths (not the barrels) so the heavy wizard/steps/layout stay
// out of the main bundle; the lightweight context providers remain eager.
const SelfOnboarding = lazyRoute(() =>
  import('@/containers/SelfOnboarding/SelfOnboarding').then((m) => ({ default: m.SelfOnboarding }))
);
const WelcomeOnBoarding = lazyRoute(() =>
  import('@/containers/SelfOnboarding/steps').then((m) => ({ default: m.Welcome }))
);
const OnboardingLayout = lazyRoute(() =>
  import('@/containers/SelfOnboarding/layout').then((m) => ({ default: m.OnboardingLayout }))
);
// Public campaign landing for paid Meta traffic — its own lazy chunk so it
// doesn't weigh on the authenticated app bundle.
const CampaignLanding = lazyRoute(() => import('@/containers/CampaignLanding'));
// Fichaje del equipo: la pantalla de fichaje es Flash con la sesión del dueño, en la recepción.
const AttendanceKiosk = lazyRoute(() => import('@/containers/Attendance/kiosk/Kiosk'));
const AttendanceScanResult = lazyRoute(() => import('@/containers/Attendance/ScanResult'));
const Attendance = lazyRoute(() => import('@/containers/Attendance/Attendance'));
const AttendanceHistory = lazyRoute(() => import('@/containers/Attendance/AttendanceHistory'));
const AcademyOnboarding = lazyRoute(() =>
  import('@/containers/AcademyOnboarding/AcademyOnboarding').then((m) => ({ default: m.AcademyOnboarding }))
);

const IgScreenshots = lazyRoute(() =>
  import('@/containers/IgScreenshots').then((module) => ({ default: module.IgScreenshots }))
);
const ActionServiceV2 = lazyRoute(() =>
  import('@/containers/ActionServiceV2').then((module) => ({ default: module.ActionServiceV2 }))
);
const Downloadables = lazyRoute(() =>
  import('@/containers/Downloadables').then((module) => ({ default: module.Downloadables }))
);
const OrderingDownloadables = lazyRoute(() =>
  import('@/containers/Downloadables').then((module) => ({ default: module.OrderingDownloadables }))
);
const ActionDownloadable = lazyRoute(() =>
  import('@/containers/ActionDownloadable').then((module) => ({ default: module.ActionDownloadable }))
);
const Products = lazyRoute(() =>
  import('@/containers/Products').then((module) => ({ default: module.Products }))
);
const OrderingProducts = lazyRoute(() =>
  import('@/containers/Products').then((module) => ({ default: module.OrderingProducts }))
);
const Ideas = lazyRoute(() => import('@/containers/Ideas').then((module) => ({ default: module.Ideas })));
const ClientForms = lazyRoute(() => import('@/containers/ClientForms/ClientForms'));
const ClientFields = lazyRoute(() => import('@/containers/ClientFields/ClientFields'));
const FieldsMigrationPage = lazyRoute(() => import('@/containers/ClientFields/MigrationPage'));
const FieldEditorPage = lazyRoute(() => import('@/containers/ClientFields/FieldEditorPage'));
const CheckoutPreviewPage = lazyRoute(() => import('@/containers/ClientFields/CheckoutPreview'));
const ClientFichaPage = lazyRoute(() => import('@/containers/ClientFields/ClientFichaPage'));
const ProductsPending = lazyRoute(() =>
  import('@/containers/Products').then((module) => ({ default: module.PendingDeliveries }))
);
const ActionProduct = lazyRoute(() =>
  import('@/containers/ActionProduct').then((module) => ({ default: module.ActionProduct }))
);
const AdjustmentsProducts = lazyRoute(() =>
  import('@/containers/AdjustmentsProducts').then((module) => ({ default: module.AdjustmentsProducts }))
);
// "Ingresar venta" — POS manual de productos sueltos (sin reserva). Lazy como el
// resto de las acciones standalone; generateRoute ya envuelve en <Suspense>.
const ProductSale = lazyRoute(() =>
  import('@/containers/ProductSale').then((module) => ({ default: module.ProductSale }))
);
const AdjustProductPrice = lazyRoute(() =>
  import('@/containers/AdjustmentsProducts').then((module) => ({ default: module.AdjustProductPrice }))
);
const AdjustProductStock = lazyRoute(() =>
  import('@/containers/AdjustmentsProducts').then((module) => ({ default: module.AdjustProductStock }))
);
// Gated wrappers — when the catalog products add-on isn't active, accessing any
// products route redirects straight to the onboarding stepper (same UX as the
// comisiones flow). No lock screen in between.
const ProductsGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <Products />;
};
const OrderingProductsGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <OrderingProducts />;
};
// Comunidad es un asset del catálogo, no un add-on: no hay onboarding de
// activación en Facturación — crear la comunidad activa todo del lado del server
// (cyclone enciende el flag del plan al crear/publicar).
const CommunityGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.COMMUNITY) return <Navigate to="/" replace />;
  return <Community />;
};
// Con la ficha V2 prendida el negocio ya está migrado y cyclone rechaza toda escritura al
// modelo viejo: /fichas dejaba crear una plantilla que terminaba en error. La búsqueda de
// funciones y el widget de configuración siguen apuntando acá, así que se redirige en la ruta.
const ClientFormsGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (flags.CLIENT_FIELDS_V2) return <Navigate to="/datos-clientes" replace />;
  return <ClientForms />;
};
const ProductsPendingGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <ProductsPending />;
};
const ActionProductGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <ActionProduct />;
};
const ProductSaleGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <ProductSale />;
};
const AdjustmentsProductsGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <AdjustmentsProducts />;
};
const AdjustProductPriceGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <AdjustProductPrice />;
};
// Landing-activity (Cofashion) is flag-gated; anyone without the flag is bounced
// to the dashboard. Backend also 403s, but the redirect avoids a flash of the page.
const LandingActivityGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.LANDING_ACTIVITY) return <Navigate to="/" replace />;
  return <LandingActivity />;
};
const AdjustProductStockGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.CATALOG_PRODUCTS_ACTIVE) return <Navigate to="/facturacion/catalogo-onboarding" replace />;
  return <AdjustProductStock />;
};
// Gestión de Caja is a beta rollout (CASH_REGISTER flag). Anyone without access is
// bounced to the dashboard so the routes can't be reached by URL.
const CashRegisterGated: React.FC = () => {
  const flags = useFeatureFlags();
  // Con Finanzas activo, la Caja vive adentro del container nuevo (misma pantalla,
  // nueva casa). El deep-link viejo sigue funcionando vía redirect.
  if (flags.FINANZAS_ACTIVE && flags.CASH_REGISTER) return <Navigate to="/finanzas/caja" replace />;
  if (!flags.CASH_REGISTER) return <Navigate to="/" replace />;
  return <CashRegister />;
};
const CashRegisterHistoryGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (flags.FINANZAS_ACTIVE && flags.CASH_REGISTER) return <Navigate to="/finanzas/caja" replace />;
  if (!flags.CASH_REGISTER) return <Navigate to="/" replace />;
  return <CashRegisterHistory />;
};
// Asistencia: con Finanzas Pro y sólo para el dueño del negocio (el backend responde 403 al resto).
const AttendanceGated: React.FC<{ history?: boolean }> = ({ history }) => {
  const flags = useFeatureFlags();
  const { session } = useAuth();
  const isOwner = !!session?.vendor && session.vendor.venue?.vendor_owner_id === session.vendor.id;
  if (!isOwner) return <Navigate to="/equipo" replace />;
  // El fichaje viene con Pro: el dueño sin Pro cae en el onboarding que lo explica y lo activa.
  if (!flags.TEAM_ATTENDANCE) return <Navigate to="/facturacion/comisiones-onboarding" replace />;
  return history ? <AttendanceHistory /> : <Attendance />;
};
const AttendanceHistoryGated: React.FC = () => <AttendanceGated history />;
const AttendanceKioskGated: React.FC = () => {
  const flags = useFeatureFlags();
  const { session } = useAuth();
  const isOwner = !!session?.vendor && session.vendor.venue?.vendor_owner_id === session.vendor.id;
  if (!flags.TEAM_ATTENDANCE || !isOwner) return <Navigate to="/" replace />;
  return <AttendanceKiosk />;
};
// Con Finanzas activo Y el add-on contratado, Comisiones vive en /finanzas/comisiones.
const TeamPaymentsGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.REVENUE_SHARE_ACTIVE) return <Navigate to="/equipo" replace />;
  if (flags.FINANZAS_ACTIVE && flags.REVENUE_SHARE_ACTIVE)
    return <Navigate to="/finanzas/comisiones" replace />;
  return <TeamPayments />;
};
// Módulo Finanzas — mitad pública, mitad add-on. La sección la ve todo dueño (el staff
// nunca). Las cuatro de ventas funcionan sin contratar nada; las cuatro del add-on llevan
// al onboarding que lo explica y lo activa. El backend hace el mismo corte.
const FinanzasGated: React.FC = () => {
  const flags = useFeatureFlags();
  const { tab } = useParams<{ tab?: string }>();
  if (!flags.FINANZAS) return <Navigate to="/" replace />;
  // Comisiones necesita equipo (gate ESTRUCTURAL, no comercial): sin él la sección se ve
  // bloqueada en el rail y abre el modal para sumar el primer profesional. Por URL vuelve
  // al resumen — el backend además la deja afuera con `requireRevenueShareAccess`.
  if (tab === 'comisiones' && !flags.REVENUE_SHARE) return <Navigate to="/finanzas" replace />;
  // Resumen, Ventas, Ventas por cobrar y Ventas cobradas son públicas: la historia de la
  // plata del negocio no se cobra aparte. Sólo las cuatro del add-on piden entitlement, y
  // sin él llevan al onboarding en vez de desaparecer.
  const esDelAddOn = !!tab && (FINANZAS_ADDON_TABS as string[]).includes(tab);
  if (esDelAddOn && !flags.FINANZAS_ACTIVE) {
    return <Navigate to="/facturacion/comisiones-onboarding" replace />;
  }
  return <Finanzas />;
};
const DynamicPricingGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.DYNAMIC_PRICING) return <Navigate to="/" replace />;
  return <DynamicPricing />;
};
const DynamicPricingRuleGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.DYNAMIC_PRICING) return <Navigate to="/" replace />;
  return <DynamicPricingRulePage />;
};
const OffersGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.OFFERS) return <Navigate to="/" replace />;
  return <Offers />;
};
const OfferEditorGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.OFFERS) return <Navigate to="/" replace />;
  return <OfferEditor />;
};
const LoyaltyGated: React.FC = () => {
  const flags = useFeatureFlags();
  // Con Premios, Fidelización deja de existir para ese negocio: es el mismo programa.
  if (flags.PREMIOS) return <Navigate to="/premios" replace />;
  if (!flags.LOYALTY) return <Navigate to="/" replace />;
  return <Loyalty />;
};
const PremiosGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.PREMIOS) return <Navigate to="/" replace />;
  return <Premios />;
};
const PremioEditorGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.PREMIOS) return <Navigate to="/" replace />;
  return <PremioEditor />;
};
const PostBuilderGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.AVAILABILITY_SHARE) return <Navigate to="/contenido-ig" replace />;
  return <PostBuilder />;
};
const HistoriasGated: React.FC = () => {
  const flags = useFeatureFlags();
  if (!flags.TU_MES) return <Navigate to="/analytics" replace />;
  return <Historias />;
};
/** Las rutas viejas siguen llegando (checklist, notificaciones, enlaces guardados): van al nuevo lugar. */
const LegacyRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};
const ReviewsGated: React.FC = () => {
  const flags = useFeatureFlags();
  const { session } = useAuth();
  if (!flags.REVIEWS || session?.is_staff) return <Navigate to="/" replace />;
  return <Reviews />;
};
/** Vuelta de Google al conectar la ficha: del dueño, como Reseñas (la API responde 404 al equipo). */
const GoogleBusinessCallbackGated: React.FC = () => {
  const flags = useFeatureFlags();
  const { session } = useAuth();
  if (!flags.REVIEWS || session?.is_staff) return <Navigate to="/" replace />;
  return <GoogleBusinessCallback />;
};
const OnlineCourses = lazyRoute(() =>
  import('@/containers/OnlineCourses').then((module) => ({ default: module.OnlineCourses }))
);
// Lo que el profesional COMPRÓ, no lo que vende (eso es OnlineCourses, en Catálogo).
const CoursePlayer = lazyRoute(() =>
  import('@/containers/MyCourses/CoursePlayer').then((module) => ({ default: module.CoursePlayer }))
);
const MyCourses = lazyRoute(() =>
  import('@/containers/MyCourses').then((module) => ({ default: module.MyCourses }))
);
const MyCommunities = lazyRoute(() =>
  import('@/containers/MyCommunities').then((module) => ({ default: module.MyCommunities }))
);
const OrderingOnlineCourses = lazyRoute(() =>
  import('@/containers/OnlineCourses/OrderingOnlineCourses').then((module) => ({
    default: module.OrderingOnlineCourses
  }))
);
const ActionOnlineCourse = lazyRoute(() =>
  import('@/containers/ActionOnlineCourse').then((module) => ({ default: module.ActionOnlineCourse }))
);
const Subscriptions = lazyRoute(() =>
  import('@/containers/Subscriptions').then((module) => ({ default: module.Subscriptions }))
);
const OrderingSubscriptions = lazyRoute(() =>
  import('@/containers/Subscriptions').then((module) => ({ default: module.OrderingSubscriptions }))
);
const ActionSubscription = lazyRoute(() =>
  import('@/containers/ActionSubscription').then((module) => ({ default: module.ActionSubscription }))
);
// `/referidos`: la compuerta decide entre la vista nueva (beta) y la de siempre.
const Invitations = lazyRoute(() =>
  import('@/containers/Referrals').then((module) => ({ default: module.ReferralsGate }))
);
const Analytics = lazyRoute(() =>
  import('@/containers/Analytics').then((module) => ({ default: module.Analytics }))
);
const AnalyticsV2 = lazyRoute(() =>
  import('@/containers/AnalyticsV2').then((module) => ({ default: module.AnalyticsV2 }))
);
// Analítica v2 detrás de ANALYTICS_V2. Apagado, /analytics sigue siendo la Analítica de siempre.
const AnalyticsGated: React.FC = () => {
  const flags = useFeatureFlags();
  return flags.ANALYTICS_V2 ? <AnalyticsV2 /> : <Analytics />;
};

// El precalentado de /analytics tiene que bajar la versión que AnalyticsGated va a montar.
// RouterContent lo mantiene al día con el flag (antes del warm de arranque).
let analyticsV2Active = false;
const setAnalyticsV2Active = (active: boolean) => {
  analyticsV2Active = active;
};
const AnalyticsGatedPreload = {
  preload: () => (analyticsV2Active ? AnalyticsV2.preload() : Analytics.preload())
};

const Notifications = lazyRoute(() =>
  import('@/containers/Notifications').then((module) => ({ default: module.Notifications }))
);

const PlanRedirect = lazyRoute(() =>
  import('@/containers/PlanRedirect').then((module) => ({ default: module.PlanRedirect }))
);
const Coupons = lazyRoute(() =>
  import('@/containers/Coupons').then((module) => ({ default: module.Coupons }))
);
const DynamicPricing = lazyRoute(() =>
  import('@/containers/Marketing/DynamicPricing').then((module) => ({ default: module.DynamicPricing }))
);
const DynamicPricingRulePage = lazyRoute(() =>
  import('@/containers/Marketing/DynamicPricing').then((module) => ({
    default: module.DynamicPricingRulePage
  }))
);
const OfferEditor = lazyRoute(() =>
  import('@/containers/Marketing/Offers/OfferEditor').then((module) => ({ default: module.OfferEditor }))
);
const Offers = lazyRoute(() =>
  import('@/containers/Marketing/Offers').then((module) => ({ default: module.Offers }))
);
const Loyalty = lazyRoute(() =>
  import('@/containers/Marketing/Loyalty').then((module) => ({ default: module.Loyalty }))
);
const Premios = lazyRoute(() =>
  import('@/containers/Marketing/Premios').then((module) => ({ default: module.Premios }))
);
const PremioEditor = lazyRoute(() =>
  import('@/containers/Marketing/Premios').then((module) => ({ default: module.PremioEditor }))
);
// Contenido IG: el hub, la guía para reservar (ex Instructivo IG) y el armador de posteos
// (ex Compartir horarios). Se importan los archivos (no el index) a propósito: cada chunk
// queda nombrado por su componente y el armador no arrastra el hub.
const ContenidoIG = lazyRoute(() => import('@/containers/Marketing/ContenidoIG/ContenidoIG'));
const PostBuilder = lazyRoute(() => import('@/containers/Marketing/ContenidoIG/PostBuilder'));
const Historias = lazyRoute(() => import('@/containers/Historias/Historias'));
const Reviews = lazyRoute(() =>
  import('@/containers/Reviews').then((module) => ({ default: module.Reviews }))
);
const Tracking = lazyRoute(() =>
  import('@/containers/Marketing/Tracking').then((module) => ({ default: module.Tracking }))
);
const GiftCards = lazyRoute(() =>
  import('@/containers/GiftCards').then((module) => ({ default: module.GiftCards }))
);
const GiftCard = lazyRoute(() =>
  import('@/containers/GiftCard').then((module) => ({ default: module.GiftCard }))
);
const GiftCardDirect = lazyRoute(() =>
  import('@/containers/GiftCardDirect').then((module) => ({ default: module.GiftCardDirect }))
);
const Plans = lazyRoute(() => import('@/containers/Plans').then((module) => ({ default: module.Plans })));
const PersonalInfo = lazyRoute(() =>
  import('@/containers/Settings/PersonalInfo').then((module) => ({ default: module.PersonalInfo }))
);
const Notification = lazyRoute(() =>
  import('@/containers/Settings/Notification').then((module) => ({ default: module.Notification }))
);
const Calendar = lazyRoute(() =>
  import('@/containers/Settings/Calendar').then((module) => ({ default: module.Calendar }))
);
const ClientCriteria = lazyRoute(() =>
  import('@/containers/Settings/ClientCriteria').then((module) => ({ default: module.ClientCriteria }))
);
const GodMode = lazyRoute(() =>
  import('@/containers/Settings/GodMode').then((module) => ({ default: module.GodMode }))
);
const PinSettings = lazyRoute(() =>
  import('@/containers/Settings/PinSettings').then((module) => ({ default: module.PinSettings }))
);
const Design = lazyRoute(() =>
  import('@/containers/Settings/Design').then((module) => ({ default: module.Design }))
);
const OverridesList = lazyRoute(() =>
  import('@/containers/Settings/MessagesAutomaticos').then((module) => ({
    default: module.OverridesList
  }))
);
const MessagesAutomaticos = lazyRoute(() =>
  import('@/containers/Settings/MessagesAutomaticos').then((module) => ({
    default: module.MessagesAutomaticos
  }))
);
const Billing = lazyRoute(() =>
  import('@/containers/Billing').then((module) => ({ default: module.Billing }))
);
const CompraExitosa = lazyRoute(() =>
  import('@/containers/Billing').then((module) => ({ default: module.CompraExitosa }))
);
const WhatsAppOnboarding = lazyRoute(() =>
  import('@/containers/Billing/components/WhatsAppOnboarding').then((module) => ({
    default: module.WhatsAppOnboarding
  }))
);
const WhatsAppEdit = lazyRoute(() =>
  import('@/containers/Billing/components/WhatsAppEdit').then((module) => ({
    default: module.WhatsAppEdit
  }))
);
const RevenueShareOnboarding = lazyRoute(() =>
  import('@/containers/Billing/components/RevenueShareOnboarding').then((module) => ({
    default: module.RevenueShareOnboarding
  }))
);
const RevenueShareOnboardingGated: React.FC = () => {
  const flags = useFeatureFlags();
  // El entitlement se mira UNA sola vez, al entrar. Recalculándolo en cada render,
  // activar el add-on prendía FINANZAS_ACTIVE y este mismo gate pateaba al vendor a
  // /finanzas apenas volvía la sesión: la pantalla de "Listo" del onboarding no se
  // llegaba a ver nunca. <Routes> no monta hasta que /sessions resuelve, así que el
  // valor de arranque ya es el real.
  const [hadAddOnOnEntry] = useState(() => flags.FINANZAS_ACTIVE);
  // Finanzas puede vender Gastos/Resultado sin Comisiones. La variante con
  // Comisiones sólo aparece cuando REVENUE_SHARE identifica un plan venue.
  if (!flags.REVENUE_SHARE && !flags.FINANZAS_AVAILABLE) return <Navigate to="/facturacion" replace />;
  // Evita reactivar por URL un add-on ya vigente. La pantalla destino también
  // respeta que Comisiones sólo existe en plan venue.
  if (hadAddOnOnEntry) {
    if (flags.REVENUE_SHARE_ACTIVE) return <Navigate to="/finanzas/comisiones" replace />;
    return <Navigate to="/finanzas/gastos" replace />;
  }
  return <RevenueShareOnboarding />;
};
const CatalogProductsOnboarding = lazyRoute(() =>
  import('@/containers/Billing/components/CatalogProductsOnboarding').then((module) => ({
    default: module.CatalogProductsOnboarding
  }))
);
const Community = lazyRoute(() =>
  import('@/containers/Community').then((module) => ({ default: module.Community }))
);
const NewCommunity = lazyRoute(() =>
  import('@/containers/Community/containers/NewCommunity').then((module) => ({
    default: module.NewCommunity
  }))
);
const Welcome = lazyRoute(() =>
  import('@/containers/Welcome').then((module) => ({ default: module.Welcome }))
);
const MercadoPagoCallback = lazyRoute(() =>
  import('@/containers/MercadoPagoCallback').then((module) => ({ default: module.MercadoPagoCallback }))
);
const InstagramCallback = lazyRoute(() => import('@/containers/Instagram/InstagramCallback'));
const GoogleCalendarCallback = lazyRoute(() =>
  import('@/containers/GoogleCalendarCallback').then((module) => ({ default: module.GoogleCalendarCallback }))
);
const GoogleBusinessCallback = lazyRoute(() =>
  import('@/containers/GoogleBusinessCallback').then((module) => ({ default: module.GoogleBusinessCallback }))
);
const GoogleDriveCallback = lazyRoute(() =>
  import('@/containers/GoogleDriveCallback/GoogleDriveCallback').then((module) => ({
    default: module.GoogleDriveCallback
  }))
);
const SecondSetup = lazyRoute(() =>
  import('@/containers/SecondSetup').then((module) => ({ default: module.SecondSetup }))
);
const SetupWelcome = lazyRoute(() =>
  import('@/containers/SetupWelcome').then((module) => ({ default: module.SetupWelcome }))
);

// Componentes de las rutas con carcasa que se precalientan (arranque, hover/foco/toque del
// rail, ocioso): el warm usa el `preload` del MISMO componente que monta la ruta, así llega
// resuelto y la pantalla se pinta sin suspender (ver lazyWithPreload).
registerRoutePreloads(
  (
    [
      ['/', Dashboard],
      ['/agenda', Agenda],
      ['/mi-pagina', StoreFrontBuilder],
      ['/notificaciones', Notifications],
      ['/analytics', AnalyticsGatedPreload],
      ['/analytics/:section', AnalyticsV2],
      ['/finanzas', Finanzas],
      ['/finanzas/:tab', Finanzas],
      ['/caja', CashRegister],
      ['/servicios', Services],
      ['/servicio/:id/editar', ActionServiceV2],
      ['/productos', Products],
      ['/cursos-online', OnlineCourses],
      ['/planesmensuales', Subscriptions],
      ['/descargables', Downloadables],
      ['/eventos', UniqueEvents],
      ['/comunidad', Community],
      ['/actividad', Activities],
      ['/actividad/:tab', Activities],
      ['/clientes', Clients],
      ['/clientes/segmentos', ClientSegments],
      ['/clientes/:id', ClientDetail],
      ['/clientes/ficha/:userId', ClientFichaPage],
      ['/promotores', Promoters],
      ['/datos-clientes', ClientFields],
      ['/fichas', ClientForms],
      ['/equipo', Team],
      ['/contenido-ig', ContenidoIG],
      ['/descuentos', Coupons],
      ['/precios-dinamicos', DynamicPricing],
      ['/precios-dinamicos/nueva', DynamicPricingRulePage],
      ['/precios-dinamicos/:ruleId', DynamicPricingRulePage],
      ['/ofertas', Offers],
      ['/fidelizacion', Loyalty],
      ['/premios', Premios],
      ['/premios/puntos', Premios],
      ['/premios/clientes', Premios],
      ['/premios/nuevo', PremioEditor],
      ['/premios/:premioId', PremioEditor],
      ['/resenas', Reviews],
      ['/tarjetas-de-regalo', GiftCards],
      ['/seguimiento', Tracking],
      ['/referidos', Invitations],
      ['/ideas', Ideas],
      ['/mis-cursos', MyCourses],
      ['/mis-comunidades', MyCommunities],
      ['/facturacion', Billing],
      ['/planes', Plans],
      ['/ajustes', PersonalInfo],
      ['/ajustes/info', PersonalInfo],
      ['/ajustes/notificaciones', Notification],
      ['/ajustes/calendario', Calendar],
      ['/ajustes/clientes', ClientCriteria],
      ['/ajustes/seguridad', PinSettings],
      ['/ajustes/medios-de-cobro', PaymentGateways],
      ['/ajustes/mensajes-automaticos', MessagesAutomaticos]
    ] as Array<[string, { preload: () => Promise<unknown> }]>
  ).map(([path, component]) => [path, component.preload])
);

const FLASH_URL = import.meta.env.VITE_FLASH_URL;

// Rutas que nunca llevan la carcasa (login, callbacks, onboarding/setup, pantallas
// públicas o a pantalla completa): mientras arrancan muestran el loader suelto.
const BARE_BOOT_PATH =
  /^\/(?:login|registro|callback|new\/user|logout|plans\/redirect|posts|onboarding|setup(?:-[^/]*)?|bienvenido|fichaje|test-error|mercado-pago\/callback|conectar-mercado-pago|instagram\/callback|comunidad\/crear|facturacion\/(?:whatsapp-onboarding|whatsapp-editar|comisiones-onboarding)|eventos\/[^/]+\/registro)(?:\/|$)/;

/**
 * Layout route de la carcasa (rail + header + banner + footer). Las rutas de
 * generateRoute con layout cuelgan de acá: navegar entre ellas, o pasar del
 * arranque a la app, solo cambia el <Outlet> y la carcasa no se remonta.
 * `booting` la deja montar antes de que Auth0 confirme la sesión; con sesión,
 * el guard es el mismo de PrivateRoute.
 */
const AppShell: FunctionComponent<{ booting?: boolean }> = ({ booting = false }) => {
  const { isAuthenticated } = useAuth();
  if (!booting && !isAuthenticated) return <Navigate replace to="/" />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// Warm del chunk de la ruta en la que el usuario entró, MIENTRAS esperamos auth + /sessions.
//
// Los early-return de abajo (isAuth0SessionLoaded / !session) no montan <Routes> hasta que
// la sesión resuelve, así que el import() del React.lazy de la ruta ni siquiera arrancaba:
// el chunk bajaba DESPUÉS de la sesión, en serie. Para /agenda eso son ~120 KB gz
// (Agenda + vendor-fullcalendar) esperando de gusto — el RUM de Android mostraba 3.3s
// entre el FCP y el LCP, y este es uno de los tramos.
//
// Son assets estáticos: no necesitan token ni sesión, pueden viajar en paralelo con el
// request de auth. Si el usuario nunca llega a la ruta (auth falla → /login), lo único que
// se pierde es la descarga de un chunk cacheable.
//
// Solo la ruta actual, a propósito: precalentar todo sería peor que el problema en 4G.
const RouterContent: FunctionComponent = () => {
  const redirectUri = useLocalStorage<string | null>('redirectUri', null);
  const location = useLocation();
  const { pathname } = location;
  // Landings públicas de alta: no esperan la sesión de Auth0 (tráfico frío de ads).
  const isCampaignRoute = pathname === '/comenzar' || pathname === '/comenzar-up';
  // Enlace de Mercado Pago por profesional: lo abre el profesional, sin cuenta en Ágora.
  const isProfessionalMpRoute =
    pathname === '/conectar-mercado-pago' || pathname.startsWith('/conectar-mercado-pago/');

  const { isLoading, getAccessTokenSilently } = useAuth0();
  const {
    session,
    isAuthenticated: isAppAuthenticated,
    isSessionError,
    isFetching: isSessionFetching,
    reloadSession,
    logOut
  } = useAuth();
  const isPerfTokenAuthenticated = hasPerfAccessToken();
  const flags = useFeatureFlags();
  const [isAuth0SessionLoaded, setIsAuth0SessionLoaded] = useState(false);

  // Dispara el warm una sola vez, con el pathname del landing (ver ROUTE_CHUNK_WARMERS).
  // Deps vacías: nos interesa la ruta de entrada, no seguir precalentando en cada navegación
  // (una vez montadas las rutas, el lazy ya baja su chunk solo).
  useEffect(() => {
    setAnalyticsV2Active(flags.ANALYTICS_V2);
  }, [flags.ANALYTICS_V2]);

  useEffect(() => {
    warmRouteChunk(window.location.pathname);
  }, []);

  useEffect(() => {
    initAnalytics(session || null);
  }, [session, pathname]);

  // Al salir de la familia /agenda olvidamos el día/semana/mes que el usuario
  // estaba viendo, para que reabrir la Agenda muestre hoy / semana actual / mes
  // actual. Mientras navegás DENTRO de /agenda (incl. ingresar reserva, bloquear
  // horarios, editar horarios de trabajo) la fecha se mantiene. (Pedido JP.)
  useEffect(() => {
    const inAgenda = pathname === '/agenda' || pathname.startsWith('/agenda/');
    if (!inAgenda) clearPersistedAgendaDate();
  }, [pathname]);

  const path = window.location.href.split('?')[0];

  const checkAuth0Session = useCallback(async () => {
    if (isPerfTokenAuthenticated || (isCodeCallbackAtBoot && window.location.pathname === '/callback')) {
      setIsAuth0SessionLoaded(true);
      return;
    }

    // Foto congelada en el boot (index.tsx), antes de que Auth0 tocara nada.
    const storage = getBootSnapshot();
    // Sin sesión, initAnalytics() sale en seco (`if (!session) return`) y PostHog
    // NUNCA se inicializa — que es exactamente el estado en el que estamos cuando
    // el bootstrap falla. Sin esto, el evento que explica el rebote se queda en la
    // cola en memoria y la navegación se lo lleva puesto. Es idempotente con
    // initAnalytics vía el singleton loadPosthog, y acá arranca la bajada del
    // chunk en paralelo con el silent auth en vez de después.
    initPublicAnalytics();

    try {
      await getAccessTokenSilently();
      if (storage.had_prior_session) {
        trackAuthEvent(AUTH_EVENTS.BOOTSTRAP_OK, { screen: 'App', ...storage });
      }
    } catch (error) {
      const failure = describeBootstrapFailure(error);
      const normalizePath = path.split('#')[0];
      if (
        normalizePath !== `${FLASH_URL}/login` &&
        normalizePath !== `${FLASH_URL}/registro` &&
        normalizePath !== `${FLASH_URL}/comenzar` &&
        normalizePath !== `${FLASH_URL}/posts/feed` &&
        normalizePath !== `${FLASH_URL}/posts/story` &&
        !(import.meta.env.DEV && normalizePath === `${FLASH_URL}/dev/placas`) &&
        !(import.meta.env.DEV && normalizePath === `${FLASH_URL}/dev/frost-tiers`) &&
        !(import.meta.env.DEV && normalizePath === `${FLASH_URL}/dev/turno-hover`) &&
        !(import.meta.env.DEV && normalizePath === `${FLASH_URL}/dev/agenda-gestos`) &&
        normalizePath !== `${FLASH_URL}/callback` &&
        normalizePath !== `${FLASH_URL}/new/user` &&
        !normalizePath.includes('plans/redirect') &&
        !(normalizePath.includes('eventos') && normalizePath.includes('registro')) &&
        !(normalizePath.includes('eventos') && normalizePath.includes('metricas'))
      ) {
        // NO se gatea por had_prior_session. Toda la evidencia de "sesión previa"
        // (cache de auth0, claves nuestras, cookie is.authenticated) vive en el
        // MISMO storage que la hipótesis B dice que WebKit borra — gatear ahí
        // silenciaría justo el caso que vinimos a medir, y la data mostraría 100%
        // de casos A por construcción. El vendor con el storage barrido se ve
        // igual que un anónimo desde el cliente, así que ese slice se muestrea y
        // se separa después en PostHog: el que ya tenía logins es un vendor real.
        if (sampleFailure(storage.had_prior_session)) {
          // Por sendBeacon: el assign de abajo mataría un capture normal.
          await trackEventBeacon(AUTH_EVENTS.BOOTSTRAP_FAILED, {
            category: 'auth',
            screen: 'App',
            bounced: true,
            sample_rate: storage.had_prior_session ? 1 : ANON_FAILURE_SAMPLE_RATE,
            ...failure,
            ...storage
          });
        }
        // El código escaneado viaja en `?t=`: sin la query, después del login no habría qué marcar.
        redirectUri.setValue(
          window.location.pathname.startsWith('/fichaje')
            ? `${window.location.pathname}${window.location.search}`
            : window.location.pathname
        );
        window.location.assign('/login');
      } else if (sampleFailure(storage.had_prior_session)) {
        // Ruta pública/tolerada: no rebota, pero la sesión igual se perdió.
        trackAuthEvent(AUTH_EVENTS.BOOTSTRAP_FAILED, {
          screen: 'App',
          bounced: false,
          sample_rate: storage.had_prior_session ? 1 : ANON_FAILURE_SAMPLE_RATE,
          ...failure,
          ...storage
        });
      }
    } finally {
      setIsAuth0SessionLoaded(true);
    }
  }, [getAccessTokenSilently, isPerfTokenAuthenticated, path, redirectUri]);

  // El callback de MP llega con ?code&state (el email viaja como `state`). Antes acá se
  // reescribía la URL a ?code&email con un window.location.href (recarga completa), pero esa
  // reescritura corría DESPUÉS de que MercadoPagoCallback montara y disparara el associate sin
  // email (efectos de hijos corren antes que los del padre) → 500 para casi todos los intentos
  // del 15-17/jul/2026. Ahora MercadoPagoCallback lee `state` directo y Auth0 ignora esa ruta
  // vía skipRedirectCallback (global.tsx), así que la reescritura ya no hace falta.
  useEffect(() => {
    if (isCampaignRoute || isProfessionalMpRoute) return;
    if (isLoading || isAuth0SessionLoaded) return;
    queueMicrotask(() => void checkAuth0Session());
  }, [
    checkAuth0Session,
    isAuth0SessionLoaded,
    isLoading,
    isCampaignRoute,
    isProfessionalMpRoute,
    location.search,
    path
  ]);

  // Vendor en onboarding: bajar ya los providers/gates lazy, en paralelo al chunk de
  // la pantalla, en vez de descubrirlos recién al montar la ruta. Al resto no le cuesta nada.
  const isVendorOnboarding = !!session?.vendor && !session.vendor.is_onboarding_finished;
  const isAcademyVendor = session?.vendor?.plan_name === AgoraPlanNameE.ACADEMY;
  useEffect(() => {
    if (!isVendorOnboarding) return;
    const warm = isAcademyVendor
      ? [loadAcademyOnboardingProvider()]
      : [loadOnboardingProvider(), loadOnboardingVariantSwitch()];
    // Sin manejo propio: si el chunk no llega, el lazy de la ruta falla igual que antes
    // y lo atiende el boundary de chunks (GlobalErrorBoundary).
    warm.forEach((p) => p.catch(() => {}));
  }, [isVendorOnboarding, isAcademyVendor]);

  const isScheduleConfigured = session?.vendor?.vendor_activity_tracking?.first_availability_at !== null;
  const isServiceCreated = session?.vendor?.vendor_activity_tracking?.first_service_at !== null;
  const isEventPlan = session?.vendor?.plan_name === AgoraPlanNameE.EVENT;
  const isAcademyPlan = session?.vendor?.plan_name === AgoraPlanNameE.ACADEMY;
  const isStaff = session?.is_staff;
  const isVenue = session?.is_venue;
  //users creados en versiones anteriores donde no existia la propiedad is_onboarding_finished_at or are users less than 2024-01-01
  const isOldStarterUser =
    (session?.vendor?.onboarding_finished_at === null && session.vendor.is_onboarding_finished) ||
    (session?.vendor?.created_at && session?.vendor?.created_at < '2024-01-01');
  const isStaffCreated = session?.vendor?.vendor_activity_tracking?.first_staff_created_at !== null;

  // Limpiar flag de loading si el servicio ya fue creado
  if (isServiceCreated && localStorage.getItem('cj_service_created_waiting') === 'true') {
    localStorage.removeItem('cj_service_created_waiting');
  }

  // Backward compatibility: treat undefined as completed setup (existing users)
  const isFullSetupCompleted = session?.vendor?.is_full_setup_completed !== false;

  // Un solo lugar para "este vendor pertenece a Setup 2": lo usan el guard de
  // re-entrada de /setup y los redirects de '/' y '*' del branch restringido —
  // si divergen, vuelve el ping-pong entre setups.
  // El servicio se crea y finish_first_setup se ejecuta en requests separados. Si
  // ese segundo request falla, volver atrás no puede habilitar otra creación y
  // duplicar el servicio: la existencia persistida del primero ya hace que el
  // vendor pertenezca a Setup 2. SecondSetup reconcilia luego el flag faltante.
  const hasPersistedFirstService =
    session?.has_services === true ||
    session?.vendor?.vendor_activity_tracking?.first_service_at != null ||
    localStorage.getItem('service_just_created') === 'true';
  const needsSetup2 =
    session?.vendor?.is_full_setup_completed === false &&
    (session?.vendor?.is_first_setup_completed === true || hasPersistedFirstService);

  // Terminar el setup TIENE que alcanzar para entrar a la app. Cualquier condición
  // extra acá es una trampa: el vendor que la incumple queda con
  // shouldRestrictRoutes=true y needsSetup2=false, y '/' lo manda a /setup — donde
  // el wizard rehidrata su último paso ("Crea tu primer servicio") y lo deja
  // dando vueltas para siempre. Pasó de verdad cuando medios de cobro salió del
  // Setup 1: first_payment_method_at dejó de stamparse y TODOS los vendors nuevos
  // quedaron encerrados. El paso volvió al setup, pero la condición NO vuelve acá:
  // que el paso exista no lo hace requisito para entrar.
  const shouldRestrictRoutes =
    isEventPlan || isStaff || isOldStarterUser
      ? false
      : isAcademyPlan
        ? !isFullSetupCompleted
        : !isFullSetupCompleted ||
          !isServiceCreated ||
          (session?.is_experimental ? false : !isScheduleConfigured) ||
          (isVenue && !isFullSetupCompleted ? !isStaffCreated : false);

  // Skip loader for public screenshot routes
  const isPostsRoute = pathname === '/posts/feed' || pathname === '/posts/story';

  // DEV-only: adaptive liquid-glass playground (Frost Tiers) — never ships
  if (import.meta.env.DEV && pathname === '/dev/frost-tiers') {
    return (
      <Suspense fallback={null}>
        <FrostTiersPlayground />
      </Suspense>
    );
  }

  // DEV-only: galería de estados del hover card de un turno — never ships
  if (import.meta.env.DEV && pathname === '/dev/turno-hover') {
    return (
      <Suspense fallback={null}>
        <TurnoHoverGallery />
      </Suspense>
    );
  }

  // DEV-only: harness de los gestos de la agenda (resize + crear arrastrando) — never ships
  if (import.meta.env.DEV && pathname === '/dev/agenda-gestos') {
    return (
      <Suspense fallback={null}>
        <AgendaGestureGallery />
      </Suspense>
    );
  }

  // Render posts routes immediately without waiting for auth
  if (isPostsRoute || isCampaignRoute || isProfessionalMpRoute) {
    return (
      <>
        <ScrollToTop />
        {/* Suspense obligatorio: estas rutas quedaron lazy y este early-return
            no pasa por el Suspense de generateRoute */}
        <Suspense fallback={null}>
          <Routes>
            <Route path="/comenzar" element={<CampaignLanding />} />
            <Route path="/comenzar-up" element={<AgoraUpLanding />} />
            <Route path="/posts/feed" element={<FeedPreview />} />
            <Route path="/posts/story" element={<StoryPreview />} />
            <Route path="/conectar-mercado-pago" element={<ConnectMercadoPago />} />
            <Route path="/conectar-mercado-pago/:token" element={<ConnectMercadoPago />} />
          </Routes>
        </Suspense>
      </>
    );
  }

  // Arranque con la carcasa ya en su lugar. TIENE que ser el mismo árbol que el
  // return final de abajo (div > ScrollToTop, PinEntryModal, PinProtectedRoute >
  // Routes > AppShell): así, cuando llega la sesión, React conserva rail, header y
  // banner y solo cambia el contenido. Antes había un spinner suelto sobre el lienzo
  // vacío, después la carcasa con otro spinner y al final se remontaba todo.
  const isBareBootPath = BARE_BOOT_PATH.test(pathname);
  const bootShell = (
    <div className="flex flex-col flex-1">
      <ScrollToTop />
      <PinEntryModal />
      <PinProtectedRoute>
        <Routes>
          <Route element={<AppShell booting />}>
            {/* El esqueleto de la pantalla que se está abriendo (routeSkeletons): el mismo
                que la pantalla pinta al montar, sin un genérico en el medio. */}
            <Route path="*" element={<RouteSkeleton />} />
          </Route>
        </Routes>
      </PinProtectedRoute>
    </div>
  );

  // Mientras Auth0 resuelve, la carcasa solo se adelanta para quien ya tenía sesión.
  if (isLoading || !isAuth0SessionLoaded) {
    return !isBareBootPath && getBootSnapshot().had_prior_session ? bootShell : <Loader fullHeight />;
  }

  // La sesión no cargó y ya no lo va a hacer sola (agotó los reintentos). ANTES este
  // caso caía en el loader de abajo: colgado y roto se veían idénticos, para siempre
  // y sin salida — el incidente del 12/ago fueron horas de vendors mirando esto.
  // Ahora se dice qué pasó y se ofrece reintentar sin perder la sesión de Auth0.
  if (isAppAuthenticated && !session && isSessionError) {
    return (
      <>
        <ScrollToTop />
        <div className="flex min-h-dvh items-center justify-center px-6">
          <div className="w-full max-w-sm space-y-5 text-center">
            <h1 className="agora-title text-frost-ink font-light tracking-[-0.02em]">
              No pudimos cargar tu cuenta<span className="text-frost-coral">.</span>
            </h1>
            <p className="text-fs-13-5 text-frost-ink-soft leading-relaxed">
              No pudimos traer los datos de tu cuenta. Todo lo tuyo está guardado.
            </p>
            <Button
              size="lg"
              className="w-full"
              isLoading={isSessionFetching}
              disabled={isSessionFetching}
              onClick={() => reloadSession()}
            >
              Reintentar
            </Button>
            {/* Sin esta salida la pantalla es terminal: `/logout` vive dentro del <Routes>
                que este guard no llega a montar. Hay casos que NO se curan reintentando
                (god-mode viejo en localStorage, un email de Auth0 sin usuario local), y
                sin botón quedaban encerrados igual que antes — que es lo que vinimos a
                arreglar. */}
            <button
              type="button"
              onClick={logOut}
              className="w-full py-2 text-center text-fs-12-5 font-medium text-frost-brand underline-offset-2 transition-colors hover:underline"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </>
    );
  }

  // Auth0 ready but session still loading — the shell tree from above.
  if (isAppAuthenticated && !session) {
    return isBareBootPath ? <Loader fullHeight /> : bootShell;
  }

  // Una cuenta admin (users.is_admin) sin god mode no tiene negocio propio que mostrar. ANTES
  // se la deslogueaba en silencio: con el god mode guardado entraba igual (la sesión viaja como
  // el negocio impersonado), pero si el navegador lo perdía —el 403 GOD_MODE_FORBIDDEN lo borra—
  // quedaba en un loop login→logout sin poder llegar a /ajustes/god-mode (23/sep/2026).
  if (isAppAuthenticated && session?.role === Auth0RolesE.ADMIN) {
    return (
      <>
        <ScrollToTop />
        <div className="flex min-h-dvh items-center justify-center px-6">
          <div className="w-full max-w-sm space-y-4">
            <h1 className="agora-title text-frost-ink font-light tracking-[-0.02em]">
              Elegí qué negocio ver<span className="text-frost-coral">.</span>
            </h1>
            <Suspense fallback={null}>
              <GodModeForm />
            </Suspense>
            <button
              type="button"
              onClick={logOut}
              className="w-full py-2 text-center text-fs-12-5 font-medium text-frost-brand underline-offset-2 transition-colors hover:underline"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </>
    );
  }

  // Gate TOTAL para cuentas de cliente: corta acá, antes de cualquier ruta de
  // vendor. Si lo dejáramos seguir, navegaría el dashboard entero con
  // `session.vendor === undefined` y reventaría en cualquier `.vendor.x`.
  if (isAppAuthenticated && session?.role === Auth0RolesE.CUSTOMER) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={null}>
          <CustomerAccountGate />
        </Suspense>
      </>
    );
  }

  // Con layout (el default) la ruta va DENTRO de un <Route element={<AppShell />}>,
  // que pone la carcasa y el guard de auth; sin layout se arma sola como antes.
  const generateRoute = (path: string, Component: any, layout: boolean = true) => {
    const page = (
      <Suspense fallback={layout ? <RouteSkeleton path={path} /> : <Loader fullHeight />}>
        {flags.UP_INTERNATIONAL && isUpHiddenCatalogPath(path) ? (
          <Navigate to="/servicios" replace />
        ) : (
          withReactProfiler(`route:${path}`, <Component />)
        )}
      </Suspense>
    );
    return (
      <Route
        path={path}
        element={
          <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
            {layout ? page : <PrivateRoute layout={false}>{page}</PrivateRoute>}
          </SessionLoader>
        }
      />
    );
  };

  // Blindaje del equipo: para un staff, lo que no está en la lista blanca no existe y cae en
  // su Agenda. El menú ya les mostraba solo lo suyo, pero las rutas seguían abiertas por URL
  // (medios de cobro, facturación, planes). Único punto de decisión → ver StaffRouteGuard.
  if (session?.is_staff && !isStaffAllowedPath(pathname, session?.vendor?.vendor_permissions)) {
    return <Navigate replace to="/agenda" />;
  }

  // force pushs
  return (
    <div className="flex flex-col flex-1">
      <ScrollToTop />
      <PinEntryModal />
      <PinProtectedRoute>
        <Routes>
          <Route
            path="/login"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <Login />
              </Suspense>
            }
          />
          <Route path="/plans/redirect/:user_id" element={<PlanRedirect />} />
          <Route path="/logout" element={<Logout />} />
          <Route
            path="/registro"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <Login isSignUp />
              </Suspense>
            }
          />
          <Route
            path="/callback"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <Auth0Callback />
              </Suspense>
            }
          />
          <Route
            path="/new/user"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <NewUser />
              </Suspense>
            }
          />
          <Route
            path="/test-error"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <TestErrorComponent />
              </Suspense>
            }
          />
          <Route
            path="/eventos/:id/registro"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <InfoTicket />
              </Suspense>
            }
          />
          <Route
            path="/posts"
            element={
              <Suspense fallback={<Loader fullHeight />}>
                <Posts />
              </Suspense>
            }
          />
          <Route
            path="/posts/feed"
            element={
              <Suspense fallback={null}>
                <FeedPreview />
              </Suspense>
            }
          />
          <Route
            path="/posts/story"
            element={
              <Suspense fallback={null}>
                <StoryPreview />
              </Suspense>
            }
          />

          {/* Fichaje: el profesional escanea con su celular; la pantalla de fichaje (sólo el dueño)
              se abre a pantalla completa en la computadora o tablet de la recepción.
              Sin carcasa y sin el SessionLoader de generateRoute: un integrante del equipo puede
              tener el onboarding a medias y lo mandaría a /onboarding en vez de marcar su llegada. */}
          <Route
            path="/fichaje/marcar"
            element={
              <PrivateRoute layout={false}>
                <Suspense fallback={<Loader fullHeight />}>
                  <AttendanceScanResult />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/fichaje"
            element={
              <PrivateRoute layout={false}>
                <Suspense fallback={<Loader fullHeight />}>
                  <AttendanceKioskGated />
                </Suspense>
              </PrivateRoute>
            }
          />

          {/*
            "Mis cursos" se registra ACÁ, en el bloque común, y NO con generateRoute:
            ese helper envuelve en SessionLoader, que manda a /onboarding a cualquiera
            que no lo haya terminado. Medido en prod: 361 de los 482 vendors que
            compraron un curso tienen el onboarding a medias (son compradores a los que
            el alta les fabricó un vendor). Por generateRoute, el 75% de la audiencia de
            esta pantalla nunca llegaría a verla.
          */}
          {/* Misma carcasa (AppShell) que el resto, sin el SessionLoader de generateRoute. */}
          <Route element={<AppShell />}>
            <Route
              path="/mis-cursos"
              element={
                <Suspense fallback={<ShellContentSkeleton />}>
                  <MyCourses />
                </Suspense>
              }
            />

            {/* El curso se mira acá adentro. Va en el mismo bloque común y por la misma
                razón que el listado: la mayoría de los que compraron un curso tienen el
                onboarding a medias y generateRoute los mandaría a /onboarding. */}
            <Route
              path="/mis-cursos/:courseId"
              element={
                <Suspense fallback={<ShellContentSkeleton />}>
                  <CoursePlayer />
                </Suspense>
              }
            />

            {/* Comunidades ajenas donde participa como miembro — mismo bloque común y
                misma razón que /mis-cursos: no depende del onboarding del vendor. */}
            <Route
              path="/mis-comunidades"
              element={
                <Suspense fallback={<ShellContentSkeleton />}>
                  <MyCommunities />
                </Suspense>
              }
            />
          </Route>

          {!session?.vendor?.is_onboarding_finished ? (
            <>
              {/* Academy Onboarding Flow */}
              {isAcademyPlan ? (
                <>
                  <Route
                    path="/onboarding/academy"
                    element={
                      <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                        <Suspense fallback={<Loader fullHeight />}>
                          <AcademyOnboardingProvider>
                            <Suspense fallback={<Loader fullHeight />}>
                              <AcademyOnboarding />
                            </Suspense>
                          </AcademyOnboardingProvider>
                        </Suspense>
                      </SessionLoader>
                    }
                  />
                  <Route path="/onboarding" element={<Navigate replace to="/onboarding/academy" />} />
                  <Route path="/" element={<Navigate replace to="/onboarding/academy" />} />
                </>
              ) : (
                <>
                  {/* Standard Onboarding Flow */}
                  <Route
                    path="/onboarding"
                    element={
                      <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                        <Suspense fallback={<Loader fullHeight />}>
                          <OnboardingProvider>
                            <Outlet />
                          </OnboardingProvider>
                        </Suspense>
                      </SessionLoader>
                    }
                  >
                    <Route
                      path={''}
                      element={
                        <Suspense fallback={<Loader fullHeight />}>
                          <OnboardingWelcomeOrSkip
                            control={
                              <Suspense fallback={<Loader fullHeight />}>
                                <WelcomeOnBoarding />
                              </Suspense>
                            }
                          />
                        </Suspense>
                      }
                    />
                    <Route
                      path={'wizard'}
                      element={
                        // El wizard releva el nombre en su propio paso (BusinessProfile,
                        // luego de Ubicación).
                        <Suspense fallback={<Loader fullHeight />}>
                          <OnboardingVariantSwitch
                            control={
                              <Suspense fallback={<Loader fullHeight />}>
                                <OnboardingLayout>
                                  <SelfOnboarding />
                                </OnboardingLayout>
                              </Suspense>
                            }
                          />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route path="/" element={<Navigate replace to="/onboarding" />} />
                </>
              )}

              <Route
                path="/mercado-pago/callback"
                element={
                  <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                    <PrivateRoute layout={false}>
                      <MercadoPagoCallback />
                    </PrivateRoute>
                  </SessionLoader>
                }
              />
              <Route
                path="/google-calendar/callback"
                element={
                  <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                    <PrivateRoute layout={false}>
                      <GoogleCalendarCallback />
                    </PrivateRoute>
                  </SessionLoader>
                }
              />
            </>
          ) : shouldRestrictRoutes ? (
            <>
              <Route path="/bienvenido" element={<Welcome />} />
              <Route
                path="/setup"
                element={
                  // Guard de re-entrada: con Setup 1 ya completo, /setup redirige a
                  // Setup 2. Antes el wizard quedaba re-jugable (estado en localStorage)
                  // y el gesto "atrás" desde Setup 2 depositaba al vendor acá: en las
                  // grabaciones del 4-5/jul hay vendors re-completando Setup 1 entero.
                  needsSetup2 ? (
                    <Navigate replace to="/setup-2" />
                  ) : (
                    <Suspense fallback={<Loader fullHeight />}>
                      <CustomerJourneySetup />
                    </Suspense>
                  )
                }
              />
              {/* Mismo wrapper (SessionLoader + PrivateRoute) que su gemela del branch
                  completo — antes quedaba montada "pelada" y renderizaba con sesión
                  a medio resolver. */}
              {generateRoute('/setup-2', SecondSetup, false)}
              <Route
                path="/setup/academy"
                element={
                  <Suspense fallback={<Loader fullHeight />}>
                    <AcademySetup />
                  </Suspense>
                }
              />
              <Route
                path="/setup-bienvenido"
                element={
                  <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                    <PrivateRoute layout={false}>
                      <Suspense fallback={<Loader fullHeight />}>
                        <SetupWelcome />
                      </Suspense>
                    </PrivateRoute>
                  </SessionLoader>
                }
              />
              <Route element={<AppShell />}>
                {generateRoute('/mi-pagina', StoreFrontBuilder)}
                {generateRoute('/agenda/ingresar-reserva', ActionReservation)}
                {generateRoute('/ajustes/god-mode', GodMode)}
                {/* Academy setup: allow payment gateway access */}
                {isAcademyPlan && generateRoute('/ajustes/medios-de-cobro', PaymentGateways)}
              </Route>
              <Route
                path="/"
                element={
                  <Navigate
                    replace
                    to={isAcademyPlan ? '/setup/academy' : needsSetup2 ? '/setup-2' : '/setup'}
                  />
                }
              />
              <Route path="/mercado-pago/callback" element={<MercadoPagoCallback />} />
              <Route
                path="*"
                element={
                  <Navigate
                    replace
                    to={isAcademyPlan ? '/setup/academy' : needsSetup2 ? '/setup-2' : '/setup'}
                  />
                }
              />
            </>
          ) : (
            <>
              <Route element={<AppShell />}>
                {generateRoute('/', Dashboard)}
                {generateRoute('/mi-pagina', StoreFrontBuilder)}
                {generateRoute('/bienvenido', Welcome)}
                {generateRoute('/actividad', Activities)}
                {generateRoute('/actividad/:tab', Activities)}
                {generateRoute('/actividad-landing', LandingActivityGated)}
                {generateRoute('/notificaciones', Notifications)}
                {generateRoute('/equipo', Team)}
                {generateRoute('/equipo/pagos', TeamPaymentsGated)}
                {generateRoute('/equipo/agregar', CreateTeam)}
                {generateRoute('/equipo/asistencia', AttendanceGated)}
                {generateRoute('/equipo/asistencia/historial', AttendanceHistoryGated)}
                {session?.vendor?.venue?.vendor_owner_id === session?.vendor?.id &&
                  generateRoute('/equipo/sucursal/agregar', CreateVenue)}
                {session?.vendor?.venue?.vendor_owner_id === session?.vendor?.id &&
                  generateRoute('/equipo/sucursal/editar/:id', CreateVenue)}
                {generateRoute('/equipo/editar/:id', ActionTeam)}
                {generateRoute('/agenda/ingresar-reserva', ActionReservation)}
                {generateRoute('/agenda/horarios-de-trabajo', ActionWorkingHours)}
                {generateRoute('/agenda/bloquear-horarios', ActionBlockHours)}
                {generateRoute('/agenda', Agenda)}
                {generateRoute('/caja', CashRegisterGated)}
                {generateRoute('/caja/historial', CashRegisterHistoryGated)}
                {generateRoute('/finanzas', FinanzasGated)}
                {generateRoute('/finanzas/:tab', FinanzasGated)}
                {generateRoute('/promotores', Promoters)}
                {generateRoute('/servicio/evento/:id', EventDetails)}
                {generateRoute('/analytics', AnalyticsGated)}
                {generateRoute('/analytics/historias', HistoriasGated)}
                {generateRoute('/analytics/historias/:mes', HistoriasGated)}
                {generateRoute('/analytics/historias/:mes/balance', HistoriasGated)}
                {flags.ANALYTICS_V2 && generateRoute('/analytics/:section', AnalyticsV2)}
                {generateRoute('/eventos', UniqueEvents)}
                {generateRoute('/eventos/crear', ActionUniqueEvent)}
                {generateRoute('/eventos/:id/editar/:tab', ActionUniqueEvent)}
                {generateRoute('/eventos/:id/info/:tab', ActionUniqueEvent)}
                {generateRoute('/servicios', Services)}
                {generateRoute('/servicios/orden', OrderingServices)}
                {generateRoute('/servicios/edicion-global', AdjustmentsServices)}
                {generateRoute('/servicios/precios-y-senas', AdjustmentsServices)}
                {generateRoute('/servicios/edicion-global/precios', AdjustPrice)}
                {generateRoute('/servicios/edicion-global/seña', AdjustDownpayment)}
                {generateRoute('/servicios/edicion-global/politicas', AdjustPolicies)}
                {generateRoute('/servicios/edicion-global/direccion', AdjustAddress)}
                {generateRoute('/servicio/crear', ActionServiceV2)}
                {generateRoute('/servicio/:id/editar', ActionServiceV2)}
                {generateRoute('/descargables', Downloadables)}
                {generateRoute('/descargables/orden', OrderingDownloadables)}
                {generateRoute('/descargable/crear', ActionDownloadable)}
                {generateRoute('/descargable/:id/editar', ActionDownloadable)}
                {flags.PRODUCTS && generateRoute('/productos', ProductsGated)}
                {flags.PRODUCTS && generateRoute('/productos/orden', OrderingProductsGated)}
                {flags.PRODUCTS && generateRoute('/productos/ingresar-venta', ProductSaleGated)}
                {flags.PRODUCTS && generateRoute('/productos/pendientes', ProductsPendingGated)}
                {flags.PRODUCTS && generateRoute('/productos/edicion-global', AdjustmentsProductsGated)}
                {flags.PRODUCTS &&
                  generateRoute('/productos/edicion-global/precios', AdjustProductPriceGated)}
                {flags.PRODUCTS && generateRoute('/productos/edicion-global/stock', AdjustProductStockGated)}
                {flags.PRODUCTS && generateRoute('/producto/crear', ActionProductGated)}
                {flags.PRODUCTS && generateRoute('/producto/:id/editar', ActionProductGated)}
                {flags.IDEA_BOX && generateRoute('/ideas', Ideas)}
                {flags.CLIENT_FORMS && generateRoute('/fichas', ClientFormsGated)}
                {flags.CLIENT_FIELDS_V2 && generateRoute('/datos-clientes', ClientFields)}
                {flags.CLIENT_FIELDS_V2 && generateRoute('/datos-clientes/migracion', FieldsMigrationPage)}
                {flags.CLIENT_FIELDS_V2 && generateRoute('/datos-clientes/campos/:fieldId', FieldEditorPage)}
                {flags.CLIENT_FIELDS_V2 &&
                  generateRoute('/datos-clientes/previsualizar', CheckoutPreviewPage)}
                {flags.CLIENT_FIELDS_V2 && generateRoute('/clientes/ficha/:userId', ClientFichaPage)}
                {generateRoute('/cursos-online', OnlineCourses)}
                {generateRoute('/cursos-online/orden', OrderingOnlineCourses)}
                {generateRoute('/curso-online/crear', ActionOnlineCourse)}
                {generateRoute('/curso-online/:id/editar', ActionOnlineCourse)}
                {generateRoute('/planesmensuales', Subscriptions)}
                {generateRoute('/planesmensuales/orden', OrderingSubscriptions)}
                {generateRoute('/planesmensuales/crear', ActionSubscription)}
                {generateRoute('/planesmensuales/:id/editar', ActionSubscription)}
                {generateRoute('/referidos', Invitations)}
                {generateRoute('/contenido-ig', ContenidoIG)}
                {generateRoute('/contenido-ig/guia', IgScreenshots)}
                {generateRoute('/contenido-ig/posteo', PostBuilderGated)}
                {generateRoute('/contenido-ig/tu-mes', () => (
                  <LegacyRedirect to="/analytics/historias" />
                ))}
                {/* El QR de «Tu mes» apunta acá: Robin reclama /analytics/* en el celular y perdería la selección. */}
                {generateRoute('/historias', () => (
                  <LegacyRedirect to="/analytics/historias" />
                ))}
                {generateRoute('/imagenes-ig', () => (
                  <LegacyRedirect to="/contenido-ig/guia" />
                ))}
                {generateRoute('/compartir-horarios', () => (
                  <LegacyRedirect to="/contenido-ig/posteo" />
                ))}
                {generateRoute('/seguimiento', Tracking)}
                {generateRoute('/ajustes/integraciones', Tracking)}
                {generateRoute('/descuentos', Coupons)}
                {generateRoute('/descuentos/crear', Coupon)}
                {generateRoute('/descuentos/editar/:id', Coupon)}
                {generateRoute('/precios-dinamicos', DynamicPricingGated)}
                {generateRoute('/precios-dinamicos/nueva', DynamicPricingRuleGated)}
                {generateRoute('/precios-dinamicos/:ruleId', DynamicPricingRuleGated)}
                {flags.OFFERS && generateRoute('/ofertas', OffersGated)}
                {flags.OFFERS && generateRoute('/ofertas/nueva', OfferEditorGated)}
                {flags.OFFERS && generateRoute('/ofertas/:offerId', OfferEditorGated)}
                {generateRoute('/fidelizacion', LoyaltyGated)}
                {flags.PREMIOS && generateRoute('/premios', PremiosGated)}
                {flags.PREMIOS && generateRoute('/premios/puntos', PremiosGated)}
                {flags.PREMIOS && generateRoute('/premios/clientes', PremiosGated)}
                {flags.PREMIOS && generateRoute('/premios/nuevo', PremioEditorGated)}
                {flags.PREMIOS && generateRoute('/premios/:premioId', PremioEditorGated)}
                {flags.REVIEWS && generateRoute('/resenas', ReviewsGated)}
                {flags.REVIEWS && generateRoute('/google-business/callback', GoogleBusinessCallbackGated)}
                {generateRoute('/tarjetas-de-regalo', GiftCards)}
                {generateRoute('/tarjetas-de-regalo/crear', GiftCard)}
                {generateRoute('/tarjetas-de-regalo/editar/:id', GiftCard)}
                {generateRoute('/tarjetas-de-regalo/enviar', GiftCardDirect)}
                {generateRoute('/planes', Plans)}
                {generateRoute('/ajustes', PersonalInfo)}
                {generateRoute('/ajustes/medios-de-cobro', PaymentGateways)}
                {generateRoute('/ajustes/medios-de-cobro/mercadopago', MercadoPago)}
                {generateRoute('/ajustes/medios-de-cobro/efectivo', Cash)}
                {generateRoute('/ajustes/medios-de-cobro/transferencia', BankTransfer)}
                {generateRoute('/ajustes/info', PersonalInfo)}
                {generateRoute('/ajustes/seguridad', PinSettings)}
                {generateRoute('/ajustes/diseno', Design)}
                {generateRoute('/ajustes/notificaciones', Notification)}
                {generateRoute('/ajustes/calendario', Calendar)}
                {generateRoute('/ajustes/clientes', ClientCriteria)}
                {/* Beta-gated: messages templates feature is hidden behind the MESSAGES_TEMPLATES flag */}
                {flags.MESSAGES_TEMPLATES &&
                  generateRoute('/ajustes/mensajes-automaticos', MessagesAutomaticos)}
                {flags.MESSAGES_TEMPLATES &&
                  generateRoute('/ajustes/mensajes-automaticos/personalizados', OverridesList)}

                {generateRoute('/ajustes/god-mode', GodMode)}
                {generateRoute('/facturacion', Billing)}
                {generateRoute('/facturacion/compra-exitosa', CompraExitosa)}
                {generateRoute('/facturacion/agregar', CreateTeam)}
                {flags.PRODUCTS &&
                  generateRoute('/facturacion/catalogo-onboarding', CatalogProductsOnboarding)}
                {flags.COMMUNITY && generateRoute('/comunidad', CommunityGated)}
                {generateRoute('/google-calendar/callback', GoogleCalendarCallback)}
                {generateRoute('/google/drive/callback', GoogleDriveCallback)}
                {generateRoute('/clientes', Clients)}
                {generateRoute('/clientes/nuevo', AddClient)}
                {/* Siempre registrada: si no, `/clientes/:id` la toma como la ficha de un cliente. */}
                {generateRoute('/clientes/segmentos', ClientSegments)}
                {generateRoute('/clientes/:id', ClientDetail)}
                {generateRoute('/token-monitor', TokenMonitor)}
                {generateRoute('/mercado-pago/callback', MercadoPagoCallback)}
              </Route>
              {/* Sin carcasa: pantallas a pantalla completa, fuera de AppShell. */}
              {generateRoute('/setup-2', SecondSetup, false)}
              {generateRoute('/setup/academy', AcademySetup, false)}
              {/* Fuera del branch restringido (`isOldStarterUser` lo apaga para todos los
                  negocios sin `onboarding_finished_at`, o sea desde feb-2025) el setup sigue
                  existiendo: Robin no abre la app hasta terminarlo y su «Continuar en la web»
                  llega acá. Deriva al paso donde quedó, como el `/` del branch restringido;
                  con el setup terminado, al Inicio. */}
              <Route
                path="/setup"
                element={
                  isFullSetupCompleted ? (
                    <Navigate replace to="/" />
                  ) : isAcademyPlan ? (
                    <Navigate replace to="/setup/academy" />
                  ) : needsSetup2 ? (
                    <Navigate replace to="/setup-2" />
                  ) : (
                    <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                      <PrivateRoute layout={false}>
                        <Suspense fallback={<Loader fullHeight />}>
                          <CustomerJourneySetup />
                        </Suspense>
                      </PrivateRoute>
                    </SessionLoader>
                  )
                }
              />
              {generateRoute('/facturacion/whatsapp-onboarding', WhatsAppOnboarding, false)}
              {generateRoute('/facturacion/whatsapp-editar', WhatsAppEdit, false)}
              {generateRoute('/facturacion/comisiones-onboarding', RevenueShareOnboardingGated, false)}
              {flags.COMMUNITY && generateRoute('/comunidad/crear', NewCommunity, false)}
              {generateRoute('/instagram/callback', InstagramCallback, false)}
              <Route
                path="/setup-bienvenido"
                element={
                  <SessionLoader isOnboardingFinished={session?.vendor?.is_onboarding_finished}>
                    <PrivateRoute layout={false}>
                      <Suspense fallback={<Loader fullHeight />}>
                        <SetupWelcome />
                      </Suspense>
                    </PrivateRoute>
                  </SessionLoader>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<Loader fullHeight />}>
                    <NotFound />
                  </Suspense>
                }
              />
            </>
          )}
        </Routes>
      </PinProtectedRoute>
    </div>
  );
};

// Kept outside RouterContent so /comenzar never mounts the authenticated bootstrap.
export const Router: FunctionComponent = () => {
  return (
    <TransitionBrowserRouter>
      <RouterContent />
    </TransitionBrowserRouter>
  );
};
